import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, absUrl, streamChat } from "../lib/api";
import { useAuth } from "../lib/auth";
import { renderMarkdown } from "../lib/md";
import { toast } from "sonner";
import {
  Plus, Send, Sparkles,
  Search, Edit3, Archive, Trash2, LogOut, Brain, Menu, X, Copy, RefreshCw,
  Globe, Paperclip, ChevronDown, ShieldCheck, MessageSquare, Code2, Volume2, VolumeX
} from "lucide-react";
import PaywallModal from "../components/PaywallModal";
import { playStartChime, isSoundMuted, setSoundMuted } from "../lib/sound";

export default function ChatPage() {
  const { user, logout, imageLimit, premium, refreshLimits } = useAuth();
  const nav = useNavigate();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [q, setQ] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [muted, setMuted] = useState(isSoundMuted());
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [memories, setMemories] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activePersona, setActivePersona] = useState(null);
  const fileInputRef = useRef(null);
  const fileMode = useRef("photo"); // photo | video | file
  const scrollRef = useRef(null);

  const loadConvs = async () => {
    const r = await api.get("/conversations");
    setConvs(r.data.conversations);
  };
  useEffect(() => { loadConvs(); }, []);

  const openConv = async (id) => {
    setActiveId(id);
    setSidebarOpen(false);
    setStreamText("");
    const r = await api.get(`/conversations/${id}/messages`);
    setMessages(r.data.messages);
    setActivePersona(r.data.persona || null);
  };

  const newChat = () => { setActiveId(null); setMessages([]); setStreamText(""); setSidebarOpen(false); setActivePersona(null); };

  const renameConv = async (id) => {
    const title = prompt("New title");
    if (!title) return;
    await api.patch(`/conversations/${id}`, { title });
    loadConvs();
  };
  const archive = async (id) => { await api.post(`/conversations/${id}/archive`); loadConvs(); if (activeId === id) newChat(); };
  const del = async (id) => { if (!window.confirm("Delete this conversation?")) return; await api.delete(`/conversations/${id}`); loadConvs(); if (activeId === id) newChat(); };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamText]);

  const openFile = (mode) => {
    fileMode.current = mode;
    if (fileInputRef.current) {
      fileInputRef.current.accept = mode === "photo" ? "image/*" : mode === "video" ? "video/*" : ".pdf,.txt,.md,.csv,.json,.docx";
      fileInputRef.current.click();
    }
    setAttachOpen(false);
  };

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    for (const f of files) {
      // If image and free user is out, show paywall
      if (f.type.startsWith("image/") && !premium && imageLimit.remaining <= 0) { setPaywall(true); return; }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPendingFiles((p) => [...p, r.data]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      }
    }
  };

  const removeFile = (id) => setPendingFiles((p) => p.filter((f) => f.id !== id));

  const send = async () => {
    if (!input.trim() && pendingFiles.length === 0) return;
    if (busy) return;
    const text = input.trim();
    const file_ids = pendingFiles.map((f) => f.id);
    setInput(""); setBusy(true); setStreamText("");
    setMessages((m) => [...m, { role: "user", text, files: pendingFiles.map((f) => ({ id: f.id, name: f.name, mime: f.mime })) }]);
    setPendingFiles([]);

    let acc = "";
    let firstToken = true;
    try {
      await streamChat({ conversation_id: activeId, text, file_ids, web_search: webSearch },
        (tok) => {
          if (firstToken) { firstToken = false; playStartChime(); }
          acc += tok; setStreamText(acc);
        },
        async (done) => {
          const cleanText = acc.replace(/\[\[(IMAGE|VIDEO):[^\]]+\]\]/gi, "").trim();
          setMessages((m) => [...m, { role: "assistant", text: cleanText, id: done.message_id }]);
          setStreamText("");
          if (!activeId && done.conversation_id) { setActiveId(done.conversation_id); loadConvs(); }
          else { loadConvs(); }
          refreshLimits();
          try {
            const cid = done.conversation_id || activeId;
            if (cid) {
              const cr = await api.get(`/conversations/${cid}/messages`);
              if (cr.data.persona && cr.data.persona !== activePersona) setActivePersona(cr.data.persona);
            }
          } catch {}
        },
        (err) => { toast.error(err || "Chat failed"); });
    } catch (e) {
      toast.error(e.message || "Failed");
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!activeId || busy) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("conversation_id", activeId);
      const r = await api.post("/chat/regenerate", fd);
      // Remove last assistant and append new
      setMessages((m) => {
        const arr = [...m];
        while (arr.length && arr[arr.length - 1].role === "assistant") arr.pop();
        arr.push({ role: "assistant", text: r.data.text, id: r.data.message_id });
        return arr;
      });
    } catch (e) { toast.error("Regenerate failed"); } finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const openMemory = async () => {
    setMemoryOpen(true);
    try { const r = await api.get("/memory"); setMemories(r.data.memories); } catch {}
  };
  const addMemory = async () => {
    const content = prompt("Save a memory (e.g. 'I'm studying Java classes & objects')");
    if (!content) return;
    await api.post("/memory", { content });
    const r = await api.get("/memory"); setMemories(r.data.memories);
  };
  const delMemory = async (id) => { await api.delete(`/memory/${id}`); setMemories((m) => m.filter((x) => x.id !== id)); };

  const filteredConvs = useMemo(() => {
    if (!q.trim()) return convs;
    return convs.filter((c) => c.title.toLowerCase().includes(q.toLowerCase()));
  }, [convs, q]);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#07080E]">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-[280px] glass-strong flex flex-col transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="sidebar">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-400 flex items-center justify-center"><Brain className="w-4 h-4 text-white" strokeWidth={2.5} /></div>
            <div>
              <div className="text-sm font-black tracking-tight aura-gradient-text">AfterMind AI</div>
              <div className="text-[9px] uppercase tracking-[0.24em] text-slate-500">Lakshya Soni</div>
            </div>
          </div>
          <button className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
        </div>

        <div className="px-3">
          <button data-testid="new-chat-btn" onClick={newChat} className="aura-btn-primary w-full py-2.5 rounded-xl text-white font-semibold flex items-center justify-center gap-2 text-sm"><Plus className="w-4 h-4" /> New chat</button>
        </div>

        <div className="px-3 mt-3 relative">
          <Search className="w-4 h-4 absolute left-6 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="search-chats" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats" className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-100 outline-none focus:border-violet-400" />
        </div>

        <div className="mt-4 flex-1 overflow-y-auto px-2 space-y-1">
          {filteredConvs.map((c) => (
            <div key={c.id} className={`group flex items-center gap-1 rounded-lg px-2 py-2 cursor-pointer text-sm ${activeId === c.id ? "bg-violet-500/15 text-violet-100" : "hover:bg-white/5 text-slate-300"}`} onClick={() => openConv(c.id)} data-testid={`conv-${c.id}`}>
              <div className="flex-1 truncate">{c.title}</div>
              <button onClick={(e) => { e.stopPropagation(); renameConv(c.id); }} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-white"><Edit3 className="w-3.5 h-3.5" /></button>
              <button onClick={(e) => { e.stopPropagation(); archive(c.id); }} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-white"><Archive className="w-3.5 h-3.5" /></button>
              <button onClick={(e) => { e.stopPropagation(); del(c.id); }} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {filteredConvs.length === 0 && <div className="text-center text-xs text-slate-500 mt-6">No conversations yet</div>}
        </div>

        <div className="p-3 border-t border-white/5 space-y-1">
          <button data-testid="memory-btn" onClick={openMemory} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5"><Brain className="w-4 h-4" /> Memory</button>
          {user?.admin && <button data-testid="admin-link" onClick={() => nav("/admin")} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5"><ShieldCheck className="w-4 h-4" /> Admin</button>}
          <div className="relative">
            <button onClick={() => setMenuOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-cyan-400 flex items-center justify-center text-xs font-bold text-white">{user?.name?.[0]?.toUpperCase() || "A"}</div>
              <div className="flex-1 text-left truncate">
                <div className="text-slate-100 text-sm truncate">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{premium ? "Premium" : "Free"} · {imageLimit.remaining ?? "∞"}/{imageLimit.limit || "∞"} img</div>
              </div>
              <ChevronDown className="w-4 h-4" />
            </button>
            {menuOpen && (
              <div className="absolute bottom-full mb-2 left-0 right-0 glass-strong rounded-xl p-1.5">
                <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5"><LogOut className="w-4 h-4" /> Sign out</button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col relative min-w-0">
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-white/5">
          <button onClick={() => setSidebarOpen(true)} data-testid="menu-btn"><Menu className="w-6 h-6 text-slate-200" /></button>
          <div className="text-sm font-bold aura-gradient-text">AfterMind AI</div>
          <button onClick={newChat}><Plus className="w-5 h-5 text-slate-200" /></button>
        </header>

        {/* Workspace switcher + persona pill */}
        <div className="px-3 lg:px-8 pt-3 lg:pt-5">
          <div className="max-w-3xl mx-auto flex flex-wrap items-center gap-1.5 pb-2" data-testid="workspace-switcher">
            {activePersona && (
              <button
                data-testid="persona-pill"
                onClick={async () => {
                  if (!activeId) return;
                  await api.post(`/conversations/${activeId}/persona`, { persona: null });
                  setActivePersona(null);
                  toast.success("Persona cleared");
                }}
                title="Click to clear persona"
                className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap bg-violet-500/20 border border-violet-400/40 text-violet-100"
              >
                <Sparkles className="w-3.5 h-3.5" /> {activePersona[0].toUpperCase() + activePersona.slice(1)} mode
                <X className="w-3 h-3 opacity-70" />
              </button>
            )}
            {[
              { id: "chat", label: "Chat", icon: MessageSquare, action: () => newChat() },
              { id: "research", label: "Research", icon: Globe, action: () => { setWebSearch(true); setInput("Research the latest on "); } },
              { id: "code", label: "Code", icon: Code2, action: () => setInput("Debug this code and explain the fix:\n\n```\n\n```") },
            ].map((t) => (
              <button key={t.id} data-testid={`ws-${t.id}`} onClick={t.action} className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition bg-white/5 border border-white/8 text-slate-300 hover:border-violet-400/50 hover:text-violet-200">
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 lg:px-8 py-8 space-y-6" data-testid="messages">
          {messages.length === 0 && !streamText && (
            <div className="max-w-2xl mx-auto text-center py-10 slide-up">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-400 flex items-center justify-center shadow-2xl shadow-violet-500/40 mb-5">
                <Brain className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
              <div className="text-xs uppercase tracking-[0.28em] text-violet-300 font-semibold">AfterMind AI · Created by Lakshya Soni</div>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight mt-3 aura-gradient-text">Hi, I'm AfterMind.</h2>
              <p className="text-slate-400 mt-3 text-sm">I was created by Lakshya Soni. Tell me what you want to create.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-8">
                {[
                  "Create a 30-second premium ad for my brand",
                  "Make an Instagram post about our launch",
                  "Build a presentation about AI in medicine",
                  "Analyze this PDF and explain it",
                ].map((s, i) => (
                  <button key={i} onClick={() => setInput(s)} className="glass text-left px-4 py-3 rounded-xl text-sm text-slate-300 hover:border-violet-400/40 transition">{s}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`max-w-3xl mx-auto flex gap-3 slide-up ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${m.role}`}>
              {m.role === "assistant" && <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-400 flex items-center justify-center shrink-0 mt-0.5"><Sparkles className="w-3.5 h-3.5 text-white" /></div>}
              <div className={`${m.role === "user"
                ? "max-w-[85%] rounded-2xl px-4 py-2.5 bg-violet-500/15 border border-violet-400/20 text-slate-100"
                : "flex-1 min-w-0"}`}>
                {m.files?.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {m.files.map((f, j) => (
                      <div key={j} className="text-xs px-2 py-1 bg-white/5 rounded-md text-slate-300 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {f.name}</div>
                    ))}
                  </div>
                )}
                {m.video_url ? (
                  <video src={m.video_url} controls className="rounded-xl mt-1 max-w-full" />
                ) : m.image_url ? (
                  <img src={m.image_url} alt="generated" className="rounded-xl mt-1 max-w-md" />
                ) : (
                  <div className="md-content text-slate-100 text-[15px]" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />
                )}
                {m.role === "assistant" && !m.video_url && !m.image_url && !m.pending && (
                  <div className="flex items-center gap-3 mt-1.5 opacity-60 hover:opacity-100 transition">
                    <button onClick={() => copy(m.text)} className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy</button>
                    <button onClick={regenerate} className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Regenerate</button>
                  </div>
                )}
                {m.pending && m.video_job && (
                  <button
                    data-testid={`cancel-video-${m.video_job}`}
                    onClick={async () => { try { await api.post(`/video/cancel/${m.video_job}`); } catch {} }}
                    className="mt-2 inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25"
                  >
                    Cancel render
                  </button>
                )}
              </div>
            </div>
          ))}

          {streamText && (
            <div className="max-w-3xl mx-auto flex gap-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-400 flex items-center justify-center shrink-0 mt-0.5"><Sparkles className="w-3.5 h-3.5 text-white" /></div>
              <div className="flex-1 min-w-0">
                <div className="md-content text-slate-100 text-[15px]" dangerouslySetInnerHTML={{ __html: renderMarkdown(streamText) + '<span class="blink">▊</span>' }} />
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="p-3 lg:p-5">
          <div className="max-w-3xl mx-auto">
            {pendingFiles.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {pendingFiles.map((f) => (
                  <div key={f.id} className="glass px-2.5 py-1.5 rounded-lg text-xs text-slate-200 flex items-center gap-2">
                    <Paperclip className="w-3.5 h-3.5 text-violet-300" />
                    {f.name}
                    <button onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-400"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="glass-strong rounded-3xl p-3 flex flex-col gap-2">
              <textarea
                data-testid="composer-input"
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Message AfterMind…"
                className="w-full bg-transparent px-3 py-2 text-slate-100 text-[16px] outline-none resize-none placeholder:text-slate-500 max-h-64 leading-relaxed"
                style={{ minHeight: 64 }}
              />
              <div className="flex items-center gap-1.5">
                <div className="relative">
                  <button data-testid="attach-btn" onClick={() => setAttachOpen((v) => !v)} className="w-10 h-10 rounded-full aura-btn-ghost flex items-center justify-center"><Plus className="w-5 h-5" /></button>
                  {attachOpen && (
                    <div className="absolute bottom-full mb-2 left-0 glass-strong rounded-2xl p-1.5 w-56 z-10">
                      {[
                        { icon: Paperclip, label: "Attach file / PDF", action: () => openFile("file"), testid: "attach-file" },
                      ].map((it, i) => (
                        <button key={i} data-testid={it.testid} onClick={it.action} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-slate-200 hover:bg-violet-500/15">
                          <it.icon className="w-4 h-4 text-violet-300" /> {it.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <input ref={fileInputRef} onChange={onFileChange} type="file" multiple className="hidden" />
                </div>

                <button data-testid="web-btn" onClick={() => setWebSearch((v) => !v)} title="Web research mode" className={`w-10 h-10 rounded-full flex items-center justify-center ${webSearch ? "bg-cyan-500/25 border border-cyan-400/40 text-cyan-200" : "aura-btn-ghost"}`}><Globe className="w-5 h-5" /></button>
                <button data-testid="sound-btn" onClick={() => { const n = !muted; setMuted(n); setSoundMuted(n); }} title={muted ? "Sound off" : "Sound on"} className="w-10 h-10 rounded-full aura-btn-ghost flex items-center justify-center">{muted ? <VolumeX className="w-5 h-5 text-slate-500" /> : <Volume2 className="w-5 h-5" />}</button>

                <div className="flex-1" />

                <button data-testid="send-btn" disabled={busy} onClick={send} className="aura-btn-primary h-10 w-10 rounded-full flex items-center justify-center text-white"><Send className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="mt-2 text-center text-[10px] text-slate-500 tracking-wide">AfterMind can make mistakes. Verify important information. Not a doctor or lawyer.</div>
          </div>
        </div>
      </main>

      {paywall && <PaywallModal onClose={() => setPaywall(false)} />}

      {memoryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setMemoryOpen(false)} data-testid="memory-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div className="relative glass-strong rounded-3xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2"><Brain className="w-5 h-5 text-violet-300" /> Memory</h3>
              <button onClick={() => setMemoryOpen(false)} className="text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-3">AfterMind remembers these to personalise responses. Only you can see them.</p>
            <div className="space-y-2 mb-4">
              {memories.map((m) => (
                <div key={m.id} className="glass rounded-lg px-3 py-2 flex items-start gap-2 text-sm text-slate-200">
                  <div className="flex-1">{m.content}</div>
                  <button onClick={() => delMemory(m.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {memories.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No memories yet.</div>}
            </div>
            <button onClick={addMemory} data-testid="add-memory-btn" className="aura-btn-primary w-full py-2.5 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add memory</button>
          </div>
        </div>
      )}
    </div>
  );
}
